import { useEffect } from "react";
import { Card } from "react-bootstrap";
import { useSearchParams, useLocation } from "react-router-dom";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import Cookies from "js-cookie";

import "./Shipment.scss";
import DataTable from "components/organisms/DataTable/DataTable";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import ColumnFilter from "components/organisms/DataTable/components/ColumnFilter";
import ListViewTour from "components/tours/listView/listViewTour";
import ManageAccessDialog from "components/molecules/TopNav/common/ManageAccess/ManageAccessDialog";
import Filter from "components/organisms/Filter/Filter";
import { getDateParams } from "helpers/commonHelpers";
import useCommonShipmentListing from "pages/common/Shipments/hooks/useCommonShipmentListing";
import useShipmentFilters from "hooks/filters/useShipmentFilters";
import usePermissions from "hooks/usePermissions";
import ExportTable from "./ExportTable";
import initialFilters from "./InitialFilters";
import getShipperShipmentColumns from "./ShipmentColumns";

const SHIPPER_SHIPMENT_LIST_DEFAULT_COLUMN_ORDER = [
  "shipment",
  "type",
  "payment_location",
  "freight_mode",
  "awarded_provider",
  "route",
  "status",
];

function Shipments() {
  const intl = useIntl();
  const location: any = useLocation();
  const [searchParams] = useSearchParams();
  const status: string = searchParams.get("display_status") || "";
  const { config: filterConfig } = useShipmentFilters({ status, type: "shipper" });
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");
  const can_consolidate_orders = JSON.parse(permissions || "{}")?.permissions?.quoting?.can_consolidate_orders ?? false;
  const can_create_multidrop_quote_requests =
    JSON.parse(permissions || "{}")?.company_shipper_settings?.can_create_multidrop_quote_requests ?? false;

  const {
    data,
    totalArchiveData,
    instance,
    archiveLoading,
    isLoading,
    showGuestAccessDialog,
    selectedShipmentId,
    handleStatusChange,
    handleRowClick,
    handleCloseManageAccessDialog,
    DISPLAY_STATUS,
    onColumnOrderChange,
  } = useCommonShipmentListing({
    getShipmentColumns,
    columnOrder: SHIPPER_SHIPMENT_LIST_DEFAULT_COLUMN_ORDER,
    archiveFilters: (tableInstance) => ({
      ...(tableInstance.state.globalFilter.payment_location?.length && {
        payment_location: tableInstance.state.globalFilter.payment_location.join(","),
      }),
      ...(tableInstance.state.globalFilter.requested_pickup_date?.type &&
        getDateParams(tableInstance.state.globalFilter.requested_pickup_date, "requested_pickup_date")),
      ...(tableInstance.state.globalFilter.quoted_pickup_date?.type &&
        getDateParams(tableInstance.state.globalFilter.quoted_pickup_date, "quoted_pickup_date")),
      ...(tableInstance.state.globalFilter.expected_pickup_date?.type &&
        getDateParams(tableInstance.state.globalFilter.expected_pickup_date, "expected_pickup_date")),
      ...(tableInstance.state.globalFilter.pol_locode?.length && {
        pol_locode: tableInstance.state.globalFilter.pol_locode,
      }),
      ...(tableInstance.state.globalFilter.pod_locode?.length && {
        pod_locode: tableInstance.state.globalFilter.pod_locode,
      }),
      ...(tableInstance.state.globalFilter.awarded_provider?.length && {
        awarded_provider: tableInstance.state.globalFilter.awarded_provider,
      }),
      ...(tableInstance.state.globalFilter.requested_delivery_date?.type &&
        getDateParams(tableInstance.state.globalFilter.requested_delivery_date, "requested_delivery_date")),
      ...(tableInstance.state.globalFilter.quoted_delivery_date?.type &&
        getDateParams(tableInstance.state.globalFilter.quoted_delivery_date, "quoted_delivery_date")),
      ...(tableInstance.state.globalFilter.expected_delivery_date?.type &&
        getDateParams(tableInstance.state.globalFilter.expected_delivery_date, "expected_delivery_date")),
      ...(tableInstance.state.globalFilter.created_by?.length && {
        created_by: tableInstance.state.globalFilter.created_by,
      }),
      ...(tableInstance.state.globalFilter.origin_name?.length && {
        origin_name: tableInstance.state.globalFilter.origin_name,
      }),
      ...(tableInstance.state.globalFilter.destination_name?.length && {
        destination_name: tableInstance.state.globalFilter.destination_name,
      }),
    }),
  });

  useEffect(() => {
    if (!location.state) return;
    instance.setGlobalFilter(() => ({
      ...initialFilters(location),
    }));
  }, [location.state]);

  function getShipmentColumns(intl: IntlShape, canCreateQuote: boolean, handleOpenGuestAccessDialog: Function) {
    return getShipperShipmentColumns(
      intl,
      canCreateQuote,
      handleOpenGuestAccessDialog,
      user_type,
      can_consolidate_orders,
      can_create_multidrop_quote_requests,
    );
  }

  const tabItems = [
    {
      eventKey: DISPLAY_STATUS.ALL,
      title: `${intl.formatMessage({
        id: "tabs.all",
        defaultMessage: "All",
      })} (${data.all.length})`,
    },
    {
      eventKey: DISPLAY_STATUS.BOOKED,
      title: `${intl.formatMessage({
        id: "tabs.booked",
        defaultMessage: "Booked",
      })} (${data[DISPLAY_STATUS.BOOKED].length})`,
    },
    {
      eventKey: DISPLAY_STATUS.SHIPPING,
      title: `${intl.formatMessage({
        id: "tabs.inTransit",
        defaultMessage: "In Transit",
      })} (${data[DISPLAY_STATUS.SHIPPING].length})`,
    },
    {
      eventKey: DISPLAY_STATUS.DELAYED,
      title: `${intl.formatMessage({
        id: "tabs.delayed",
        defaultMessage: "Delayed",
      })} (${data[DISPLAY_STATUS.DELAYED].length})`,
    },
    {
      eventKey: DISPLAY_STATUS.DELIVERED,
      title: `${intl.formatMessage({
        id: "tabs.delivered",
        defaultMessage: "Delivered",
      })} (${data[DISPLAY_STATUS.DELIVERED].length})`,
    },
    {
      eventKey: DISPLAY_STATUS.INCOMPLETE,
      title: `${intl.formatMessage({
        id: "tabs.incomplete",
        defaultMessage: "Incomplete",
      })} (${data[DISPLAY_STATUS.INCOMPLETE].length})`,
    },
    {
      eventKey: DISPLAY_STATUS.ARCHIVED,
      title: intl.formatMessage({
        id: "tabs.archived",
        defaultMessage: "Archived",
      }),
    },
  ];

  // Hide Booked, Shipping, and Archived tabs for guest users
  const filteredTabItems = tabItems.filter((tab) =>
    Cookies.get("is_guest") === "true"
      ? tab.eventKey !== DISPLAY_STATUS.ALL &&
        tab.eventKey !== DISPLAY_STATUS.BOOKED &&
        tab.eventKey !== DISPLAY_STATUS.ARCHIVED
      : true,
  );

  return (
    <>
      <ListViewTour />
      <ManageAccessDialog
        shipmentId={selectedShipmentId}
        isOpen={showGuestAccessDialog}
        close={handleCloseManageAccessDialog}
      />
      <Card body className="shadow-none shipment-container">
        <div className="d-flex align-items-center mb-4">
          <h3 className="mr-3">
            <FormattedMessage id="general.shipments" defaultMessage="Shipments" />
          </h3>
          <GlobalTableSearch
            currentFilters={instance.state.globalFilter}
            setFilters={instance.setGlobalFilter}
            eventContext="Shipment List"
            filterKey="query"
          />
        </div>
        <DataTable
          autoPagination
          tableInstance={instance}
          totalData={status === DISPLAY_STATUS.ARCHIVED ? totalArchiveData : data[!status ? "all" : status].length}
          isLoading={status === DISPLAY_STATUS.ARCHIVED ? archiveLoading : isLoading}
          onRowClick={handleRowClick}
          onColumnOrderChange={onColumnOrderChange}
          headerLeft={
            <PillTabs id="shipment-statuses" activeKey={status} onSelect={handleStatusChange} tabItems={filteredTabItems} />
          }
          headerRight={
            Cookies.get("is_guest") === "true" ? null : (
              <div className="d-flex align-items-center">
                <Filter
                  presetType="shipment"
                  eventContext="Shipment List"
                  currentFilters={instance.state.globalFilter}
                  setFilters={instance.setGlobalFilter}
                  config={filterConfig}
                />
                <div className="ml-3">
                  <ColumnFilter columns={instance.allColumns} eventContext="Shipment List" listViewName="shipments" />
                </div>
                <div className="ml-3">
                  <ExportTable shipmentData={instance} />
                </div>
              </div>
            )
          }
          withoutBody
        />
      </Card>
    </>
  );
}

export default Shipments;
